import { useMutation } from '@tanstack/react-query';
import { Button, ErrorNote, InfoNote } from '../../../components/ui';
import { api } from '../../../lib/api';
import { WidgetCard } from '../primitives';

/**
 * The ledger's own health check.
 *
 * This re-sums every movement and compares it against the cached balances, which
 * is O(all movements) — so it runs only when asked, never on page load. A healthy
 * result is a quiet line; a drift is loud, because it should be impossible.
 */
export function LedgerIntegrityWidget() {
  const reconcile = useMutation({ mutationFn: api.inventory.reconcile });

  return (
    <WidgetCard
      title="Ledger integrity"
      description="Re-sums every movement and checks it against the cached balances."
    >
      <Button onClick={() => reconcile.mutate()} disabled={reconcile.isPending}>
        {reconcile.isPending ? 'Checking…' : 'Run check'}
      </Button>

      <div className="mt-3">
        {reconcile.data ? (
          reconcile.data.healthy ? (
            <InfoNote>
              <strong>Ledger intact.</strong> {reconcile.data.balanceRows} balance(s) across{' '}
              {reconcile.data.movementRows} movement(s) agree exactly.
            </InfoNote>
          ) : (
            <ErrorNote>
              <strong>Ledger drift detected.</strong> {reconcile.data.discrepancies.length}{' '}
              balance(s) disagree with their movements and {reconcile.data.orphanMovements.length}{' '}
              movement group(s) have no balance row. Investigate before trusting these numbers.
            </ErrorNote>
          )
        ) : null}
        {reconcile.error ? <ErrorNote>Could not run the check.</ErrorNote> : null}
        {!reconcile.data && !reconcile.error ? (
          <p className="text-xs text-ink-muted">Not run in this session.</p>
        ) : null}
      </div>
    </WidgetCard>
  );
}
